const hostingServicesData = [
  {
    title: "Managed Hosting",
    description:
      "Secure, scalable managed infrastructure with uptime SLAs, proactive monitoring, automated backups, and regular patching.",
    bullets: ["Backups", "Security", "Scaling", "24/7"],
    minHeight: 400,
  },
  {
    title: "Migrations",
    description:
      "Plan, rehearse, and execute zero‑downtime migrations with blue/green cutovers, rollback plans, and post‑migration validation.",
    bullets: ["Blue/Green", "Rollback", "DNS", "Validation"],
    minHeight: 400,
  },
  {
    title: "Cost Optimization",
    description:
      "Analyze usage to right‑size resources, adopt savings plans/reserved instances, schedule non‑prod shutdowns, and track budgets/alerts.",
    bullets: ["FinOps", "Spot", "Schedules", "Budgets"],
    minHeight: 400,
  },
  {
    title: "Observability",
    description:
      "Unified logs, metrics, traces, and SLO dashboards with alerting, runbooks, and error budgets to keep systems healthy.",
    bullets: ["Dashboards", "Alerts", "Tracing", "SLOs"],
    minHeight: 400,
  },
  {
    title: "Security",
    description:
      "Defense‑in‑depth with WAF, backups, IAM least‑privilege policies, encryption at rest/in transit, and automated compliance checks.",
    bullets: ["WAF", "Backups", "IAM", "Policies"],
    minHeight: 400,
  },
  {
    title: "Support",
    description:
      "SLA‑backed support with 24/7 on‑call, incident response, root‑cause analysis, and quarterly service reviews.",
    bullets: ["SLAs", "Runbooks", "Incidents", "Reports"],
    minHeight: 400,
  },
  {
    title: "Global CDN & Edge",
    description:
      "Accelerate delivery worldwide with CDN caching, edge logic, image optimization, and smart cache‑control strategies.",
    bullets: ["CDN Rules", "Edge Functions", "Image Opt", "Cache Strategy"],
    minHeight: 400,
  },
  {
    title: "Managed Databases",
    description:
      "Provisioned, highly available databases with automated backups, upgrades, monitoring, and performance tuning.",
    bullets: ["HA/Replicas", "Backups", "Upgrades", "Tuning"],
    minHeight: 400,
  },
  {
    title: "Compliance & Governance",
    description:
      "Codified guardrails, continuous auditing, and policy enforcement to meet CIS/ISO/SOC standards across environments.",
    bullets: ["CIS/ISO", "IAM Guardrails", "Audit Trails", "SoD"],
    minHeight: 400,
  },
];

export default hostingServicesData;

// Additional datasets centralized here for Hosting page sections
export const hostingReliabilityData = {
  eyebrow: "Reliability",
  title: "SLA‑Backed Uptime, Secure by Default",
  subTitle:
    "We run, monitor, and harden your infrastructure 24×7 so you can ship features instead of firefighting. Our managed hosting blends proactive monitoring, auto‑healing, and rapid incident response.",
  metrics: [
    { label: "Uptime", value: "99.95%+" },
    { label: "Response Time", value: "< 200ms" },
    { label: "Recovery (MTTR)", value: "< 15m" },
    { label: "Coverage", value: "24×7×365" },
  ],
  features: [
    {
      title: "Proactive Monitoring",
      description:
        "Real‑time metrics, synthetic checks, alerting, and on‑call rotation. We catch issues before users do.",
      icon: "fa-solid fa-chart-line",
    },
    {
      title: "Auto‑Scaling & Healing",
      description:
        "Scale on demand and auto‑replace unhealthy instances to maintain performance during traffic spikes.",
      icon: "fa-solid fa-arrows-rotate",
    },
    {
      title: "Security Hardening",
      description:
        "WAF, DDoS protection, least‑privilege IAM, patching, and CIS benchmarks baked into your stack.",
      icon: "fa-solid fa-shield-halved",
    },
    {
      title: "Backups & DR",
      description:
        "Point‑in‑time backups, multi‑region replication, and tested disaster recovery runbooks.",
      icon: "fa-solid fa-database",
    },
    {
      title: "Cost Optimization",
      description:
        "Right‑sizing, savings plans, and lifecycle policies to keep cloud bills predictable and lean.",
      icon: "fa-solid fa-receipt",
    },
    {
      title: "SLA & Reporting",
      description:
        "Monthly reports with uptime, incidents, and recommendations, under a clear SLA.",
      icon: "fa-solid fa-file-shield",
    },
  ],
};

export const hostingMigrationData = {
  eyebrow: "Migration",
  title: "Zero‑Downtime Migration, Done Right",
  subTitle:
    "We move your workloads with a validated plan, dry‑runs, observability, and rollback safety—so your users never notice.",
  steps: [
    {
      title: "Discovery & Audit",
      description:
        "Inventory apps, data, dependencies, SLAs, and risks. We baseline performance to set success targets.",
      badges: ["Inventory", "Risk Map", "Baseline"],
    },
    {
      title: "Landing Zone & Security",
      description:
        "Set up accounts, networks, IAM, secrets, logging, WAF, and guardrails following best practices.",
      badges: ["VPC & IAM", "WAF", "Logging"],
    },
    {
      title: "Dry‑Run & Data Sync",
      description:
        "Replica environments, blue/green or canary, and continuous data sync (CDC) with verifications.",
      badges: ["Blue/Green", "CDC", "Integrity"],
    },
    {
      title: "Cutover & Validation",
      description:
        "DNS/traffic switch, health checks, synthetic tests, and business acceptance to confirm stability.",
      badges: ["DNS Switch", "Health OK", "BAT"],
    },
    {
      title: "Optimize & Handover",
      description:
        "Rightsizing, caching, autoscaling, cost controls, runbooks, and training for your team.",
      badges: ["Autoscale", "Cost Save", "Runbooks"],
    },
  ],
  highlights: [
    {
      title: "Rollback Safety",
      description: "Reversible cutovers with checkpoints to eliminate risk.",
      icon: "fa-solid fa-rotate-left",
    },
    {
      title: "Compliance Ready",
      description:
        "HIPAA/GDPR alignment and audit‑friendly logging out of the box.",
      icon: "fa-solid fa-clipboard-check",
    },
    {
      title: "Observability",
      description: "Metrics, logs, traces, and SLO dashboards from day one.",
      icon: "fa-solid fa-eye",
    },
    {
      title: "Expert On‑Call",
      description: "SREs available 24×7 during and after migration.",
      icon: "fa-solid fa-headset",
    },
  ],
};
